import { logger } from '@/ui/utilities/instrumentation/logger'
import { useEffect, useRef } from 'react'

type EditorTrackingContext = {
	sessionId: string
	campaignId: string
}

type FirstInteraction =
	| 'template_applied'
	| 'font_applied'
	| 'design_saved'
	| 'overlay_edited'
	| 'image_edited'
	| 'layer_interacted'
	| 'tool_clicked'

export type FontSource = 'system' | 'google' | 'custom'
export type SimulatorType = 'feed' | 'story'
type DesignType = 'story' | 'feed'

export type OverlayField =
	| 'preset'
	| 'cycle_colors'
	| 'mode_toggle'
	| 'fill_mode'
	| 'fill_color'
	| 'fill_blend_mode'
	| 'mask_mode'
	| 'mask_gradient_direction'
	| 'mask_opacity'
	| 'gradient_opacity_left'
	| 'gradient_opacity_right'
	| 'gradient_handle'

export type ImageAction =
	| 'selected'
	| 'quick_upload_clicked'
	| 'quick_generate_clicked'
	| 'library_opened'
	| 'generate_prompt_submitted'

let currentEditorContext: EditorTrackingContext | null = null
const engagedSessions = new Set<string>()
const appliedFontsPerSession = new Map<string, Set<string>>()
const trackDebounceTimers = new Map<string, ReturnType<typeof setTimeout>>()
const TRACK_DEBOUNCE_MS = 2000

function scheduleDebouncedTrack(key: string, fire: () => void): void {
	const existing = trackDebounceTimers.get(key)
	if (existing) { clearTimeout(existing) }
	trackDebounceTimers.set(
		key,
		setTimeout(() => {
			trackDebounceTimers.delete(key)
			fire()
		}, TRACK_DEBOUNCE_MS),
	)
}

export function useEditorTracking(campaignId: string): void {
	const sessionIdRef = useRef<string | null>(null)
	if (sessionIdRef.current === null) {
		sessionIdRef.current = crypto.randomUUID()
	}
	const sessionId = sessionIdRef.current

	useEffect(() => {
		currentEditorContext = { sessionId, campaignId }
		logger.track('Design Simulator: Opened', {
			data: {
				session_id: sessionId,
				campaign_id: campaignId,
			},
		})
		return () => {
			currentEditorContext = null
		}
	}, [sessionId, campaignId])
}

function markEngaged(firstInteraction: FirstInteraction, simulatorType?: SimulatorType): void {
	const ctx = currentEditorContext
	if (!ctx || engagedSessions.has(ctx.sessionId)) { return }
	engagedSessions.add(ctx.sessionId)
	logger.track('Design Simulator: Engaged', {
		data: {
			session_id: ctx.sessionId,
			campaign_id: ctx.campaignId,
			first_interaction: firstInteraction,
			...(simulatorType ? { simulator_type: simulatorType } : {}),
		},
	})
}

export function trackTemplateApplied(
	templateId: string,
	templateLabel: string,
	simulatorType: SimulatorType | null,
): void {
	const ctx = currentEditorContext
	if (!ctx) { return }
	markEngaged('template_applied', simulatorType ?? undefined)
	logger.track('Design Simulator: Template applied', {
		data: {
			session_id: ctx.sessionId,
			campaign_id: ctx.campaignId,
			template_id: templateId,
			template_label: templateLabel,
			...(simulatorType ? { simulator_type: simulatorType } : {}),
		},
	})
}

export function trackFontApplied(
	fontSource: FontSource,
	fontFamily: string,
	simulatorType: SimulatorType,
): void {
	const ctx = currentEditorContext
	if (!ctx) { return }
	markEngaged('font_applied', simulatorType)

	let seen = appliedFontsPerSession.get(ctx.sessionId)
	if (!seen) {
		seen = new Set<string>()
		appliedFontsPerSession.set(ctx.sessionId, seen)
	}
	if (seen.has(fontFamily)) { return }
	seen.add(fontFamily)

	logger.track('Design Simulator: Font applied', {
		data: {
			session_id: ctx.sessionId,
			campaign_id: ctx.campaignId,
			font_source: fontSource,
			font_family: fontFamily,
			simulator_type: simulatorType,
		},
	})
}

export function trackDesignSaved(designType: DesignType, designId: string): void {
	const ctx = currentEditorContext
	if (!ctx) { return }
	markEngaged('design_saved', designType)
	logger.track('Design Simulator: Design saved', {
		data: {
			session_id: ctx.sessionId,
			campaign_id: ctx.campaignId,
			design_type: designType,
			design_id: designId,
		},
	})
}

export function trackImageUploaded(simulatorType: SimulatorType): void {
	const ctx = currentEditorContext
	if (!ctx) { return }
	logger.track('Design Simulator: Image uploaded', {
		data: {
			session_id: ctx.sessionId,
			campaign_id: ctx.campaignId,
			simulator_type: simulatorType,
		},
	})
}

export type LandingPageElement =
	| 'title'
	| 'subtitle'
	| 'body'
	| 'image'
	| 'image_crop'
	| 'logo_visibility'
	| 'logo_background_color'

export function trackLandingPageEdited(element: LandingPageElement): void {
	if (!currentEditorContext) { return }

	scheduleDebouncedTrack(`landing_page_edited::${element}`, () => {
		const ctxAtFire = currentEditorContext
		if (!ctxAtFire) { return }
		logger.track('Design Simulator: Landing page edited', {
			data: {
				session_id: ctxAtFire.sessionId,
				campaign_id: ctxAtFire.campaignId,
				element,
			},
		})
	})
}

const DEBOUNCED_OVERLAY_FIELDS: ReadonlySet<OverlayField> = new Set([
	'fill_color',
	'mask_opacity',
	'gradient_opacity_left',
	'gradient_opacity_right',
	'gradient_handle',
])

export function trackOverlayEdited(field: OverlayField, simulatorType: SimulatorType): void {
	const ctx = currentEditorContext
	if (!ctx) { return }
	markEngaged('overlay_edited', simulatorType)

	const fire = (): void => {
		const ctxAtFire = currentEditorContext
		if (!ctxAtFire) { return }
		logger.track('Design Simulator: Overlay edited', {
			data: {
				session_id: ctxAtFire.sessionId,
				campaign_id: ctxAtFire.campaignId,
				field,
				simulator_type: simulatorType,
			},
		})
	}

	if (DEBOUNCED_OVERLAY_FIELDS.has(field)) {
		scheduleDebouncedTrack(`overlay_edited::${field}::${simulatorType}`, fire)
	} else {
		fire()
	}
}

export function trackImageEdited(action: ImageAction, simulatorType: SimulatorType): void {
	const ctx = currentEditorContext
	if (!ctx) { return }
	markEngaged('image_edited', simulatorType)
	logger.track('Design Simulator: Image edited', {
		data: {
			session_id: ctx.sessionId,
			campaign_id: ctx.campaignId,
			action,
			simulator_type: simulatorType,
		},
	})
}

export type LayerControl =
	| 'content_visibility_toggled'
	| 'font_changed'
	| 'font_reset'
	| 'card_visibility_toggled'
	| 'card_color_changed'
	| 'logo_position_changed'
	| 'logo_background_color_changed'
	| 'location_position_changed'
	| 'attribution_position_changed'
	| 'stack_orientation_changed'

const DEBOUNCED_LAYER_CONTROLS: ReadonlySet<LayerControl> = new Set([
	'card_color_changed',
	'logo_background_color_changed',
])

export function trackLayerInteracted(control: LayerControl, simulatorType: SimulatorType): void {
	const ctx = currentEditorContext
	if (!ctx) { return }
	markEngaged('layer_interacted', simulatorType)

	const fire = (): void => {
		const ctxAtFire = currentEditorContext
		if (!ctxAtFire) { return }
		logger.track('Design Simulator: Layer interacted', {
			data: {
				session_id: ctxAtFire.sessionId,
				campaign_id: ctxAtFire.campaignId,
				control,
				simulator_type: simulatorType,
			},
		})
	}

	if (DEBOUNCED_LAYER_CONTROLS.has(control)) {
		scheduleDebouncedTrack(`layer_interacted::${control}::${simulatorType}`, fire)
	} else {
		fire()
	}
}

export type ToolName = 'backdrop_transform' | 'backdrop_reset' | 'download_png'

export function trackToolClicked(tool: ToolName, simulatorType: SimulatorType): void {
	const ctx = currentEditorContext
	if (!ctx) { return }
	markEngaged('tool_clicked', simulatorType)
	logger.track('Design Simulator: Tool clicked', {
		data: {
			session_id: ctx.sessionId,
			campaign_id: ctx.campaignId,
			tool,
			simulator_type: simulatorType,
		},
	})
}

export function trackCustomFontUploaded(organisationId: string, fontFamily: string): void {
	logger.track('Design Simulator: Custom font uploaded', {
		data: {
			session_id: currentEditorContext?.sessionId ?? null,
			organisation_id: organisationId,
			font_family: fontFamily,
		},
	})
}

// Exposed only for tests.
export function __setEditorContextForTests(context: EditorTrackingContext | null): void {
	currentEditorContext = context
}

export function __resetEditorTrackingStateForTests(): void {
	currentEditorContext = null
	engagedSessions.clear()
	appliedFontsPerSession.clear()
	for (const timer of trackDebounceTimers.values()) {
		clearTimeout(timer)
	}
	trackDebounceTimers.clear()
}
